import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  trackPixel,
  trackScrollDepth,
  trackHighIntentVisitor,
  trackEngagedVisitor,
} from './pixel'

// Session-scoped flags so each milestone fires at most once per session.
const FLAGS = {
  highIntent: 'mp_fired_high_intent',
  engaged: 'mp_fired_engaged',
  pageCount: 'mp_page_count',
  sessionStart: 'mp_session_start',
} as const

function once(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}

/**
 * Mount once inside the Router. Handles:
 *  - SPA PageView on route change
 *  - Scroll depth 25/50/75/90 (per session)
 *  - HighIntentVisitor (60s+ OR 3+ pages OR inquiry opened — opened is fired elsewhere)
 *  - EngagedVisitor (scroll >75% OR 90s+)
 */
export function useEngagementTracking(): void {
  const location = useLocation()

  // Initialize session start timestamp once.
  useEffect(() => {
    try {
      if (!sessionStorage.getItem(FLAGS.sessionStart)) {
        sessionStorage.setItem(FLAGS.sessionStart, String(Date.now()))
      }
    } catch {
      /* ignore */
    }
  }, [])

  // SPA PageView + page-count based HighIntent.
  const isFirst = useFirstRender()
  useEffect(() => {
    if (!isFirst) {
      trackPixel('PageView')
    }
    try {
      const count = Number(sessionStorage.getItem(FLAGS.pageCount) || '0') + 1
      sessionStorage.setItem(FLAGS.pageCount, String(count))
      if (count >= 3 && once(FLAGS.highIntent)) {
        trackHighIntentVisitor('3+ pages')
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Time-based triggers: 60s -> HighIntent, 90s -> Engaged.
  useEffect(() => {
    const t60 = setTimeout(() => {
      if (once(FLAGS.highIntent)) trackHighIntentVisitor('60s session')
    }, 60_000)
    const t90 = setTimeout(() => {
      if (once(FLAGS.engaged)) trackEngagedVisitor('90s session')
    }, 90_000)
    return () => {
      clearTimeout(t60)
      clearTimeout(t90)
    }
  }, [])

  // Scroll depth tracking.
  useEffect(() => {
    const fired = new Set<number>()
    const onScroll = () => {
      const doc = document.documentElement
      const scrollTop = window.scrollY || doc.scrollTop
      const height = doc.scrollHeight - doc.clientHeight
      if (height <= 0) return
      const pct = Math.round((scrollTop / height) * 100)
      ;([25, 50, 75, 90] as const).forEach((milestone) => {
        if (pct >= milestone && !fired.has(milestone)) {
          fired.add(milestone)
          trackScrollDepth(milestone)
          if (milestone >= 75 && once(FLAGS.engaged)) {
            trackEngagedVisitor('scroll 75%')
          }
        }
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
}

// Tiny first-render tracker.
function useFirstRender(): boolean {
  const ref = useRef(true)
  const wasFirst = ref.current
  if (ref.current) ref.current = false
  return wasFirst
}
